// src/components/LinearWeekViewDOM.jsx
import React, { useMemo } from 'react';
import LinearDayColumnDOM from './LinearDayColumnDOM'; // Component for a single day column
import Card from './ui/Card'; // Optional: Wrap in a card
import { SCHEDULE_START_MINUTE, SCHEDULE_END_MINUTE, TOTAL_SCHEDULE_MINUTES } from '../config/constants';
import { formatTimeFromMinutes, getWeekStartDate } from '../utils/helpers.ts';

function LinearWeekViewDOM({ items, days, onOpenModal, user, currentWeekStart, selectedTeacher, selectedGroups }) {
  // Calculate week start date for day numbering
  const weekStartDate = currentWeekStart || getWeekStartDate(new Date());

  // Generate time markers for the left side
  const timeMarkers = useMemo(() => {
    const markers = [];
    const interval = 60; // Show marker every hour

    for (let minutes = SCHEDULE_START_MINUTE; minutes <= SCHEDULE_END_MINUTE; minutes += interval) {
      if (minutes % 60 === 0) {
        const topPercent = Math.min(100, ((minutes - SCHEDULE_START_MINUTE) / TOTAL_SCHEDULE_MINUTES) * 100);
        markers.push({
          time: formatTimeFromMinutes(minutes),
          style: { top: `${topPercent}%` }
        });
      }
    }
    return markers;
  }, []);

  // Helper function to detect overlapping items within the same day
  const getOverlappingGroups = useMemo(() => {
    const groups = [];

    // Group items by day first
    const itemsByDay = {};
    items.forEach(item => {
      if (!item || !item.day) return;
      if (!itemsByDay[item.day]) itemsByDay[item.day] = [];
      itemsByDay[item.day].push(item);
    });

    // For each day, find overlapping groups
    Object.keys(itemsByDay).forEach(day => {
      const dayItems = itemsByDay[day];

      for (const entry of dayItems) {
        if (!entry || !entry.startMinutes || !entry.durationMinutes) continue;

        // Find if this entry overlaps with any existing group
        let addedToGroup = false;

        for (const group of groups) {
          // Check if this entry overlaps with any item in this group
          const overlapsWithGroup = group.some(groupEntry => {
            return (
              groupEntry.day === entry.day &&
              groupEntry.startMinutes !== null &&
              groupEntry.durationMinutes !== null &&
              entry.startMinutes !== null &&
              entry.durationMinutes !== null &&
              // Check time overlap: two items overlap if one starts before the other ends
              groupEntry.startMinutes < (entry.startMinutes + entry.durationMinutes) &&
              entry.startMinutes < (groupEntry.startMinutes + groupEntry.durationMinutes)
            );
          });

          if (overlapsWithGroup) {
            group.push(entry);
            addedToGroup = true;
            break;
          }
        }

        // If not added to any existing group, create a new group
        if (!addedToGroup) {
          groups.push([entry]);
        }
      }
    });

    return groups;
  }, [items]);

  // Determine dynamic grid columns based on number of days
  const numCols = Array.isArray(days) ? days.length : 5;
  const gridColsClass = (() => {
    switch (numCols) {
      case 1: return 'grid-cols-1';
      case 2: return 'grid-cols-2';
      case 3: return 'grid-cols-3';
      case 4: return 'grid-cols-4';
      case 5: return 'grid-cols-5';
      case 6: return 'grid-cols-6';
      default: return 'grid-cols-7';
    }
  })();
  const minColumnWidthPx = 160; // keep similar to previous 800px for 5 cols
  const dynamicMinWidthPx = numCols * minColumnWidthPx;

  return (
    <Card className="overflow-hidden shadow-lg border-2 border-gray-200">
      {/* Header with enhanced styling */}


      {/* Main schedule container with horizontal scroll */}
      <div className="relative bg-gray-50 overflow-x-auto overflow-y-hidden md:overflow-y-auto">
        <div className="flex" style={{ minWidth: `${80 + dynamicMinWidthPx}px` }}>
          {/* Time markers column */}
          <div className="flex-shrink-0 bg-white border-r-2 border-gray-300 relative" style={{ width: '80px', height: '800px' }}>
            {/* Time column header - removed "Time" text */}
            <div className="bg-gradient-to-b from-white to-gray-100 border-b-2 border-gray-300 h-[68px] flex items-center justify-center">
            </div>

            {/* Time markers */}
            <div className="relative h-[730px]">
              {/* Horizontal lines */}
              {timeMarkers.map(marker => (
                <div
                  key={`line-${marker.time}`}
                  className="absolute left-0 right-0 border-t border-gray-200"
                  style={marker.style}
                />
              ))}

              {/* Time labels positioned just below the lines */}
              {timeMarkers.map(marker => (
                <div
                  key={marker.time}
                  className="absolute right-2 text-right"
                  style={{
                    top: `calc(${marker.style.top} + 4px)` // Position just below the line
                  }}
                >
                  <span className="text-xs font-medium text-gray-600 bg-white px-1 py-0.5 rounded-sm">
                    {marker.time}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Days grid container */}
          <div
            className={`flex-1 grid ${gridColsClass}`}
            style={{ minWidth: `${dynamicMinWidthPx}px` }}
          >
            {days.map(day => {
              const itemsForDay = items.filter(item => item && item.day === day);
              return (
                <LinearDayColumnDOM
                  key={day}
                  day={day}
                  items={itemsForDay}
                  onOpenModal={onOpenModal}
                  user={user}
                  weekStartDate={weekStartDate}
                  selectedTeacher={selectedTeacher}
                  selectedGroups={selectedGroups}
                  overlappingGroups={getOverlappingGroups}
                />
              );
            })}
          </div>
        </div>

        {/* Subtle background grid for better visual alignment - hidden on mobile */}
        <div className="hidden md:block absolute inset-0 pointer-events-none" style={{ left: '80px' }}>
          {/* Vertical day separators */}
          <div className={`grid ${gridColsClass} h-full`}>
            {days.map((day, index) => (
              <div
                key={`bg-${day}`}
                className={`border-r border-gray-100 ${index === days.length - 1 ? 'border-r-0' : ''}`}
              />
            ))}
          </div>

          {/* Horizontal time grid lines */}
          <div className="absolute inset-0" style={{ top: '68px' }}>
            {timeMarkers.map(marker => (
              <div
                key={`grid-line-${marker.time}`}
                className="absolute left-0 right-0 border-t border-gray-100"
                style={marker.style}
              />
            ))}
          </div>
        </div>
      </div>

    </Card>
  );
}

export default LinearWeekViewDOM;